import React, {Suspense} from 'react';
import Loading from '../components/global/loading';
import Reveal from '../components/ui/reveal';

const ImageCallout = React.lazy(() => import('../components/timeline/imageCallout'));
const TimelineVideo = React.lazy(() => import('../components/timeline/timelineVideo'));
const TextCarousel = React.lazy(() => import('../components/timeline/textCarousel'));
const RelatedStory = React.lazy(() => import('../components/timeline/relatedStory'));
const TimelineCountTrigger = React.lazy(() => import('../components/timeline/timelineCountTrigger'));
const SectionHeading = React.lazy(() => import('../components/global/sectionHeading'));
const CreditCallout = React.lazy(() => import('../components/basic/creditCallout'));

const wrapInCountTigger = (
  item,
  paragraph,
  index,
  registerNodeListener,
  updateCounts,
  id
) => (
  <TimelineCountTrigger
    key={`timelineItem${paragraph.type}${index}${id}`}
    registerNodeListener={registerNodeListener}
    bombCount={
      paragraph.timeline_info && paragraph.timeline_info.number_of_bombs
    }
    updateCounts={updateCounts}
  >
    {item}
  </TimelineCountTrigger>
);

const timelineMapDataToComponents = (
  paragraphs,
  registerNodeListener,
  updateCounts,
  id
) => {
  const isSSR = typeof window === "undefined";
  return (
    paragraphs &&
    paragraphs.map((p, i) => {
      switch (p.type) {
        case 'section_heading':
          return wrapInCountTigger(
            <SectionHeading
              heading={p.section_heading}
              prefix={p.section_prefix}
              isTimeline
            />,
            p,
            i,
            registerNodeListener,
            updateCounts,
            id
          );
        case 'text_carousel':
          return wrapInCountTigger(
            <TextCarousel
              header={p.callout_heading}
              items={p.items.map(item => ({
                heading: item.callout_heading,
                src: item.image ? item.image.sizes.square_sm : '',
                original: item.image ? item.image.sizes.original : '',
                altText: item.image ? item.image.alt_text : '',
                text: item.text,
              }))}
            />,
            p,
            i,
            registerNodeListener,
            updateCounts,
            id
          );

        case 'video_callout':
          return wrapInCountTigger(
            <Reveal>
              <TimelineVideo
                heading={p.callout_heading}
                date={p.timeline_info ? p.timeline_info.year : null}
                imgSrc={p.image ? p.image.sizes.landscape_md : ''}
                videoUrl={p.video ? p.video.url : ''}
                videoId={p.video ? p.video.id : ''}
                videoBackground={
                  p.video_background ? p.video_background.url : ''
                }
                text={p.text ? p.text : ''}
                startTimeSeconds={p.start_time_seconds}
                startTimeMinutes={p.start_time_minutes}
              />
            </Reveal>,
            p,
            i,
            registerNodeListener,
            updateCounts,
            id
          );
        case 'timeline_image_text':
          return wrapInCountTigger(
            <Reveal>
              <ImageCallout
                src={p.image ? p.image.sizes.landscape_sm : ''}
                imgDimensions={p.image ? p.image.dimensions.landscape_sm : ''}
                original={p.image ? p.image.sizes.landscape_sm : ''}
                altText={p.image ? p.image.alt_text : ''}
                heading={p.callout_heading}
                text={p.text}
                date={p.timeline_info ? p.timeline_info.year : null}
                cta_link={p.cta_link}
                image_alignment_type={p.image_alignment_type}
              />
            </Reveal>,
            p,
            i,
            registerNodeListener,
            updateCounts,
            id
          );
        case 'timeline_carousel':
          return wrapInCountTigger(
            <Reveal>
              <ImageCallout
                src={p.image ? p.image.sizes.landscape_sm : ''}
                imgDimensions={p.image ? p.image.dimensions.landscape_sm : ''}
                altText={p.image ? p.image.alt_text : ''}
                heading={p.callout_heading}
                text={p.text}
                date={p.timeline_info ? p.timeline_info.year : null}
                cta_link={p.cta_link}
                image_alignment_type={p.image_alignment_type}
                items={p.images.map((item, i) => ({
                  src: item.sizes.fixed_height_portrait,
                  altText: item.alt_text,
                  caption: item.caption,
                  credit: item.credit,
                }))}
              />
            </Reveal>,
            p,
            i,
            registerNodeListener,
            updateCounts,
            id
          );
        case 'related_content_callout':
          return wrapInCountTigger(
            <Reveal>
              <RelatedStory
                src={p.image ? p.image.sizes.square_md : ''}
                label={p.callout_label}
                heading={p.callout_heading}
                text={p.text}
                cta_link={p.cta_link}
              />
            </Reveal>,
            p,
            i,
            registerNodeListener,
            undefined,
            id
          );
        case 'credit_callout':
          return (
            <div key={`timelineItem${p.type}${i}`}>
              {p.items.map((item, j) => (
                <Reveal key={`timelineItem${p.type}${i}${j}`}>
                  <CreditCallout
                    src={item.image.sizes.original}
                    alt={item.image.alt_text}
                    text={item.logo_text}
                    link={item.cta_link.url}
                  />
                </Reveal>
              ))}
            </div>
          );

        default:
          return null;
      }
    })
  ).map((paragraph, index) => {
    return(
      <>
        {!isSSR && (
            <Suspense key={`suspense${index}`} fallback={<Loading/>}>
              {paragraph}
            </Suspense>
        )}
      </>
    );
  });
};

export default timelineMapDataToComponents;
